import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import useAuth from "./hooks/useAuth";
import { PASS_VERIFY_PATH, PASS_VERIFY_PATH_EN } from "./constants/routes";
import { localizedPath, routePath } from "./utils/routeHelpers";
import { ADMIN_PERMISSION_KEYS } from "./constants/adminModules";
import AdminPinGate from "./components/AdminPinGate";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const PassWizardPage = lazy(() => import("./pages/portal/PassWizardPage"));
const PassTrackingPage = lazy(() => import("./pages/portal/PassTrackingPage"));
const PassAccountSetupPage = lazy(() => import("./pages/portal/PassAccountSetupPage"));
const PasswordResetPage = lazy(() => import("./pages/portal/PasswordResetPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PassesPage = lazy(() => import("./pages/PassesPage"));
const GuardScanPage = lazy(() => import("./pages/GuardScanPage"));
const CardsPage = lazy(() => import("./pages/CardsPage"));
const AdminPanelPage = lazy(() => import("./pages/admin/AdminPanelPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const AccessGroupsPage = lazy(() => import("./pages/admin/AccessGroupsPage"));
const WhitelistPage = lazy(() => import("./pages/admin/WhitelistPage"));
const LoginHistoryPage = lazy(() => import("./pages/admin/LoginHistoryPage"));
const DataAccessPage = lazy(() => import("./pages/admin/DataAccessPage"));
const ZonesPage = lazy(() => import("./pages/admin/ZonesPage"));
const O365ConfigPage = lazy(() => import("./pages/admin/O365ConfigPage"));
const PortalAccountsPage = lazy(() => import("./pages/admin/PortalAccountsPage"));
const AppSettingsPage = lazy(() => import("./pages/admin/AppSettingsPage"));
const MessageTemplatesPage = lazy(() => import("./pages/admin/MessageTemplatesPage"));
const CompaniesPage = lazy(() => import("./pages/admin/CompaniesPage"));
const CompaniesDirectoryPage = lazy(() => import("./pages/companies/CompaniesDirectoryPage"));
const CompanyPanelPage = lazy(() => import("./pages/company/CompanyPanelPage"));

const PageLoader = () => (
    <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
        <div
            style={{
                width: 32,
                height: 32,
                border: "3px solid var(--color-border)",
                borderTopColor: "#6366F1",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
            }}
        />
        <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
);

const HomeRedirect = () => {
    const { user, loading } = useAuth();
    if (loading) return <PageLoader />;
    return <Navigate to={user ? localizedPath("dashboard") : localizedPath("login")} replace />;
};

const App = () => (
    <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path={routePath("login")} element={<LoginPage />} />
            {/* trasa logowania rejestrowana w OBU językach (wzorzec mpracownika) */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/logowanie" element={<LoginPage />} />

            {/* Portal publiczny — osoby zewnętrzne składają i śledzą wnioski BEZ konta */}
            <Route path="/wniosek" element={<PassWizardPage />} />
            <Route path="/wniosek/status/:token" element={<PassTrackingPage />} />
            <Route path="/wniosek/konto/:token" element={<PassAccountSetupPage />} />
            {/* Reset zapomnianego hasła (konta zewnętrzne) — bez tokenu = prośba, z tokenem = ustawienie. */}
            <Route path="/reset-hasla" element={<PasswordResetPage />} />
            <Route path="/reset-hasla/:token" element={<PasswordResetPage />} />
            {/* Dawny profil portalowy — ZLIKWIDOWANY (decyzja klienta 2026-08-08): dane
                i ich edycja mieszkają w panelu „Moje dane" na pulpicie. Adres przekierowuje,
                żeby zapisane linki nie prowadziły w pustkę. */}
            <Route path="/wniosek/profil" element={<Navigate to="/pulpit" replace />} />

            <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path={routePath("dashboard")} element={<DashboardPage />} />
                    <Route path={routePath("passes")} element={<PassesPage />} />
                    <Route
                        path={routePath("guard")}
                        element={
                            <ProtectedRoute requiredPermission="GuardScan" moduleLabel="Weryfikacja QR">
                                <GuardScanPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* LINK Z KODU QR PRZEPUSTKI — „zeskanuj i masz wynik".
                        • Pod ProtectedRoute, bo token w adresie jest POŚWIADCZENIEM: niezalogowany
                          idzie na logowanie i po zalogowaniu wraca DOKŁADNIE tutaj (ProtectedRoute
                          zapisuje cel w location.state.from, LoginPage go odczytuje).
                        • BEZ requiredPermission="GuardScan", inaczej niż ekran /weryfikacja: zakres
                          odpowiedzi liczy serwer (POST /passes/verify-lookup jest [Authorize], a
                          dane zawęża wg tytułu do wniosku), więc opiekun wizyty też sprawdzi swojego
                          gościa, a osoba bez tytułu zobaczy „nie znaleziono" — nigdy cudze dane.
                        • Oba warianty językowe rejestrujemy ZAWSZE (adres jest wydrukowany na
                          przepustce i nie może zależeć od języka interfejsu skanującego). */}
                    <Route path={`${PASS_VERIFY_PATH}/:token`} element={<GuardScanPage />} />
                    <Route path={`${PASS_VERIFY_PATH_EN}/:token`} element={<GuardScanPage />} />
                    {/* KONTA PRZEPUSTEK (kartoteki + wystawianie przepustek) — zakładka
                        CODZIENNEJ pracy obsługi, nie moduł administracyjny: bez AdminPinGate
                        (decyzja klienta 2026-08-07). Dostępu pilnuje uprawnienie, a danych
                        wrażliwych audytowany endpoint /reveal. */}
                    <Route
                        path={routePath("portalAccounts")}
                        element={
                            <ProtectedRoute
                                requiredPermission={["PortalAccountsView", "PortalAccountsManage"]}
                                moduleLabel="Konta przepustek"
                            >
                                <PortalAccountsPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* FIRMY (kartoteki firm zewnętrznych) — moduł CODZIENNEJ pracy pracownika
                        Krosno Glass, dlatego BEZ AdminPinGate: PIN chroni panel administratora,
                        czyli zakładanie firm i kont, a nie zaglądanie do kartoteki. Ekran jest
                        wyłącznie do odczytu — zmiany robi się pod /admin/firmy (CompaniesManage). */}
                    <Route
                        path={routePath("companies")}
                        element={
                            <ProtectedRoute requiredPermission="CompaniesView" moduleLabel="Firmy">
                                <CompaniesDirectoryPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* PANEL FIRMY ZEWNĘTRZNEJ — celowo BEZ requiredPermission: dostępu nie
                        daje klucz uprawnienia, tylko powiązanie konta z firmą (user.companyId
                        z /users/me), a tego pilnuje sama strona i przekierowuje na pulpit,
                        gdy powiązania brak. */}
                    <Route path={routePath("company")} element={<CompanyPanelPage />} />
                    {/* Wystawianie przepustek pracownikom żyje W KARTOTECE: to ta sama
                        lista ludzi, więc dwie osobne zakładki znaczyły dwa miejsca na te same
                        dane. Stary adres przekierowujemy, żeby zapisane linki nie prowadziły
                        w pustkę. */}
                    <Route
                        path={routePath("employeePasses")}
                        element={<Navigate to={localizedPath("portalAccounts")} replace />}
                    />
                    <Route
                        path={routePath("cards")}
                        element={
                            <ProtectedRoute
                                requiredPermission={["CardView", "CardManage"]}
                                moduleLabel="Karty dostępu"
                            >
                                <CardsPage />
                            </ProtectedRoute>
                        }
                    />
                    {/* Panel administratora: sam spis modułów. Bramkuje go PIN (AdminPinGate),
                        a każdy moduł ma dodatkowo własne uprawnienie na swojej trasie. */}
                    <Route
                        path={routePath("adminPanel")}
                        element={
                            <ProtectedRoute
                                requiredPermission={ADMIN_PERMISSION_KEYS}
                                moduleLabel="Panel administratora"
                            >
                                <AdminPinGate>
                                    <AdminPanelPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={routePath("adminUsers")}
                        element={
                            <ProtectedRoute requiredPermission="UsersManage" moduleLabel="Użytkownicy">
                                <AdminPinGate>
                                    <UsersPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    {/* Grupy uprawnień — ten sam klucz co konta (UsersManage): kto nadaje
                        uprawnienia osobie, ten musi też widzieć, co daje jej ranga. */}
                    <Route
                        path={routePath("adminAccessGroups")}
                        element={
                            <ProtectedRoute requiredPermission="UsersManage" moduleLabel="Grupy uprawnień">
                                <AdminPinGate>
                                    <AccessGroupsPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={routePath("adminWhitelist")}
                        element={
                            <ProtectedRoute requiredPermission="WhitelistManage" moduleLabel="Whitelista">
                                <AdminPinGate>
                                    <WhitelistPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={routePath("adminDataAccess")}
                        element={
                            <ProtectedRoute
                                requiredPermission="DataAccessJournal"
                                moduleLabel="Dziennik dostępu do danych"
                            >
                                <AdminPinGate>
                                    <DataAccessPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={routePath("adminLoginHistory")}
                        element={
                            <ProtectedRoute requiredPermission="LoginHistoryView" moduleLabel="Historia logowań">
                                <AdminPinGate>
                                    <LoginHistoryPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={routePath("adminZones")}
                        element={
                            <ProtectedRoute requiredPermission="ZoneManage" moduleLabel="Strefy dostępu">
                                <AdminPinGate>
                                    <ZonesPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={routePath("adminCompanies")}
                        element={
                            <ProtectedRoute requiredPermission="CompaniesManage" moduleLabel="Firmy zewnętrzne">
                                <AdminPinGate>
                                    <CompaniesPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={routePath("adminO365")}
                        element={
                            <ProtectedRoute requiredPermission="ConfigManage" moduleLabel="Konfiguracja O365">
                                <AdminPinGate>
                                    <O365ConfigPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    {/* Dawny adres kartotek w panelu administratora — samo przekierowanie
                        na nową zakładkę (moduł wyszedł z /admin, patrz trasa portalAccounts). */}
                    <Route
                        path={routePath("adminPortalAccounts")}
                        element={<Navigate to={localizedPath("portalAccounts")} replace />}
                    />
                    <Route
                        path={routePath("adminMessages")}
                        element={
                            <ProtectedRoute requiredPermission="SettingsManage" moduleLabel="Treści wiadomości">
                                <AdminPinGate>
                                    <MessageTemplatesPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={routePath("adminSettings")}
                        element={
                            <ProtectedRoute requiredPermission="SettingsManage" moduleLabel="Ustawienia">
                                <AdminPinGate>
                                    <AppSettingsPage />
                                </AdminPinGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<HomeRedirect />} />
                </Route>
            </Route>
        </Routes>
    </Suspense>
);

export default App;
